import { useCallback } from 'react';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { Badge } from '@/core/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/core/components/ui/table';
import { Skeleton } from '@/core/components/ui/skeleton';
import { useImportConfigurationStore } from '../../stores/import-configuration-store';
import {
  SheetMappingItem,
  ColumnMappingItem,
  columnNumberToLetter,
  formatColumnDisplay,
} from '../../types/import-configuration-types';
import { ApplicationTypeLabels, getLanguageName } from '../../types/translation-types';

interface ColumnMappingsPanelProps {
  sheetMapping: SheetMappingItem;
  isLoading?: boolean;
}

export function ColumnMappingsPanel({ sheetMapping, isLoading }: ColumnMappingsPanelProps) {
  const { openColumnFormDialog, openDeleteDialog } = useImportConfigurationStore();

  const handleAddColumn = useCallback(() => {
    openColumnFormDialog(sheetMapping.mappingId);
  }, [sheetMapping.mappingId, openColumnFormDialog]);

  const handleEditColumn = useCallback(
    (column: ColumnMappingItem) => {
      openColumnFormDialog(sheetMapping.mappingId, column);
    },
    [sheetMapping.mappingId, openColumnFormDialog]
  );

  const handleDeleteColumn = useCallback(
    (column: ColumnMappingItem) => {
      openDeleteDialog('column', column.columnMappingId, column.languageCode);
    },
    [openDeleteDialog]
  );

  // Sort column mappings by column number
  const sortedColumns = [...(sheetMapping.columnMappings || [])].sort(
    (a, b) => a.columnNumber - b.columnNumber
  );

  // Build Excel preview
  const buildExcelPreview = () => {
    const columns: { letter: string; label: string; type: 'key' | 'desc' | 'lang' }[] = [];

    // Key column
    columns.push({
      letter: columnNumberToLetter(sheetMapping.keyColumnNumber),
      label: 'Key',
      type: 'key',
    });

    // Description column
    if (sheetMapping.descriptionColumnNumber) {
      columns.push({
        letter: columnNumberToLetter(sheetMapping.descriptionColumnNumber),
        label: 'Desc',
        type: 'desc',
      });
    }

    // Language columns
    sortedColumns.forEach(col => {
      if (col.isActive) {
        columns.push({
          letter: columnNumberToLetter(col.columnNumber),
          label: col.languageCode,
          type: 'lang',
        });
      }
    });

    // Sort by actual column letter
    columns.sort((a, b) => a.letter.localeCompare(b.letter));

    return columns;
  };

  const excelPreview = buildExcelPreview();

  return (
    <div className="p-4 space-y-4">
      {/* Sheet info header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">Sheet:</span>
            <span className="text-lg">{sheetMapping.sheetName}</span>
            <Badge variant="outline">
              {ApplicationTypeLabels[sheetMapping.applicationType] ||
                `Type ${sheetMapping.applicationType}`}
            </Badge>
          </div>
          <div className="text-sm text-muted-foreground">
            Key Column: {formatColumnDisplay(sheetMapping.keyColumnNumber)}
            {sheetMapping.descriptionColumnNumber && (
              <> | Description Column: {formatColumnDisplay(sheetMapping.descriptionColumnNumber)}</>
            )}
          </div>
        </div>
        <Button size="sm" onClick={handleAddColumn}>
          <Plus className="h-4 w-4 mr-1" />
          Add Language Column
        </Button>
      </div>

      {/* Excel preview */}
      <div className="bg-white border rounded-md p-3">
        <div className="text-xs text-muted-foreground mb-2">Excel Column Preview:</div>
        <div className="flex gap-1 overflow-x-auto">
          {excelPreview.map((col, idx) => (
            <div
              key={idx}
              className={`flex-shrink-0 px-3 py-2 text-center border rounded text-sm ${
                col.type === 'key'
                  ? 'bg-blue-100 border-blue-300'
                  : col.type === 'desc'
                  ? 'bg-gray-100 border-gray-300'
                  : 'bg-green-100 border-green-300'
              }`}
            >
              <div className="font-mono font-bold">{col.letter}</div>
              <div className="text-xs">{col.label}</div>
            </div>
          ))}
          {excelPreview.length === 0 && (
            <div className="text-muted-foreground text-sm">No columns configured</div>
          )}
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          <span className="text-muted-foreground">Loading column mappings...</span>
        </div>
      )}

      {/* Column mappings table */}
      {!isLoading && (
        <>
          {sortedColumns.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              No language columns configured. Add language columns to map Excel columns to languages.
            </div>
          ) : (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Language</TableHead>
                    <TableHead>Column</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedColumns.map(column => (
                    <TableRow key={column.columnMappingId}>
                      <TableCell>
                        <div className="font-medium">{column.languageCode}</div>
                        <div className="text-xs text-muted-foreground">
                          {getLanguageName(column.languageCode)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">
                            {columnNumberToLetter(column.columnNumber)}
                          </span>
                          <span className="text-muted-foreground">
                            (Column {column.columnNumber})
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {column.isActive ? (
                          <Badge variant="default" className="bg-green-100 text-green-800">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditColumn(column)}
                            className="h-8 w-8 p-0"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteColumn(column)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
